import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateKeyNote } from 'infrastructure/hooks/key-notes/use-update-key-note';
import { useDeleteKeyNote } from 'infrastructure/hooks/key-notes/use-delete-key-note';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { Flex } from 'antd';
import { AnimatePresence } from 'framer-motion';
import { NoteComposer } from 'components/organisms/oneplaform/key-notes-drawer/note-composer';
import { parseDateSafe } from 'utils/timeHelper';

import s from './styles.module.scss';
import { DeleteModalKeyNote } from './modal/delete-key-note';
import { HistoryModalKeyNote } from './modal/history-key-note';
import { KeyNote } from './key-note';

import type { ICreateOrUpdate } from './types';
import type { ModuleType } from 'infrastructure/enums';
import type { IKeyNoteData } from 'infrastructure/interfaces';

interface IKeyNotesContentProps {
  isDataEmpty: boolean;
  data: IKeyNoteData[] | undefined;
  module: ModuleType;
  patientGuid?: string;
  showExpired: boolean;
  onAction?: () => void;
  onEditStart?: () => void;
  externalEditingNoteGuid?: string | null;
  onEditingChange?: (guid: string | null) => void;
}

const KeyNotesContent: React.FC<IKeyNotesContentProps> = ({
  isDataEmpty,
  data,
  patientGuid,
  module,
  showExpired,
  onAction,
  onEditStart,
  externalEditingNoteGuid,
  onEditingChange,
}) => {
  const { t } = useTranslation();
  const [selectedNote, setSelectedNote] = useState<IKeyNoteData | null>(null);
  const [editingNoteGuid, setEditingNoteGuid] = useState<string | null>(null);
  const [isDeleteMode, setIsDeleteMode] = useState<boolean>(false);
  const [isHistoryMode, setIsHistoryMode] = useState<boolean>(false);
  const onCancelDelete = () => setIsDeleteMode(false);
  const { updateKeyNote, isLoading } = useUpdateKeyNote({ module });
  const { deleteKeyNote, isLoading: deleteIsLoading } = useDeleteKeyNote({
    module,
  });

  useEffect(() => {
    if (
      externalEditingNoteGuid !== undefined &&
      externalEditingNoteGuid !== editingNoteGuid
    ) {
      setEditingNoteGuid(externalEditingNoteGuid);
    }
  }, [externalEditingNoteGuid, editingNoteGuid]);

  const onSave = async (values: ICreateOrUpdate) => {
    if (patientGuid && selectedNote?.guid) {
      updateKeyNote(
        {
          patientGuid,
          module,
          note: values.note,
          expiresAt: values.expiresAt,
          noteGuid: selectedNote.guid,
          showExpired,
        },
        {
          onSuccess() {
            notificationController.success({
              message: t('messages.keynoteUpdated'),
            });
            setEditingNoteGuid(null);
            setSelectedNote(null);
            onAction?.();
          },
        },
      );
    }
  };

  const handleDeleteNote = (guid: string) => {
    if (selectedNote?.guid) {
      deleteKeyNote(
        {
          patientGuid: guid,
          noteGuid: selectedNote.guid,
          module,
          showExpired,
        },
        {
          onSuccess() {
            notificationController.info({
              message: t('messages.keynoteDeleted'),
            });
            setIsDeleteMode(false);
            onAction?.();
          },
        },
      );
      if (deleteIsLoading) {
        setIsDeleteMode(false);
      }
    }
  };

  useEffect(() => {
    setSelectedNote(null);
    setEditingNoteGuid(null);
    setIsDeleteMode(false);
    setIsHistoryMode(false);
  }, [module, patientGuid]);

  return (
    <>
      {selectedNote && isHistoryMode && patientGuid && (
        <HistoryModalKeyNote
          module={module}
          note={selectedNote}
          isOpen={isHistoryMode}
          patientGuid={patientGuid}
          onCancel={() => setIsHistoryMode(false)}
        />
      )}
      {selectedNote && patientGuid && (
        <DeleteModalKeyNote
          isLoading={deleteIsLoading}
          onCancel={onCancelDelete}
          onOk={() => handleDeleteNote(patientGuid)}
          isOpen={isDeleteMode}
        />
      )}
      {isDataEmpty ? (
        <div data-cy="patient-key-note-content" className={s['empty-note']}>
          {t('labels.noNotes')}
        </div>
      ) : (
        <Flex vertical gap={25}>
          {data?.map((keynote) => (
            <div key={`${keynote.guid}-keynote-${module}`}>
              <AnimatePresence>
                {editingNoteGuid === keynote.guid ? (
                  <NoteComposer
                    key={`edit-${keynote.guid}`}
                    initialModule={module}
                    onSave={onSave}
                    onCancel={() => {
                      setEditingNoteGuid(null);
                      setSelectedNote(null);
                      onEditingChange?.(null);
                    }}
                    isLoading={isLoading}
                    isEditMode
                    initialValues={{
                      note: keynote.note || '',
                      expiresAt: parseDateSafe(keynote.expiresAt) || null,
                    }}
                  />
                ) : (
                  <KeyNote
                    key={`view-${keynote.guid}`}
                    showActions
                    keynote={keynote}
                    module={module}
                    onEdit={() => {
                      onEditStart?.();
                      setEditingNoteGuid(keynote.guid);
                      setSelectedNote(keynote);
                      onEditingChange?.(keynote.guid);
                    }}
                    onShowHistory={() => {
                      setIsHistoryMode(true);
                      setSelectedNote(keynote);
                    }}
                    onDelete={() => {
                      setSelectedNote(keynote);
                      setIsDeleteMode(true);
                    }}
                  />
                )}
              </AnimatePresence>
            </div>
          ))}
        </Flex>
      )}
    </>
  );
};

export default KeyNotesContent;
